import { Alert, Box, CircularProgress, IconButton, Snackbar, SxProps, Typography } from '@mui/material';
import useImageTransform from '../hooks/useImageGen';
import { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { AutoFixHigh as EnhanceIcon } from '@mui/icons-material';

export interface EnhancementSettings {
   brightness: number;
   contrast: number;
   sharpness: number;
   saturation: number;
   warmth: number;
   shadows: number;
   skinSmoothing: number;
}

const AIEnhanceImageButton = ({
   Icon,
   photoUrl,
   onDone,
   photoId,
   sx,
   checkSubscription,
   preset = 'standard',
   settings,
}: {
   Icon?: React.ReactNode;
   photoUrl: string;
   onDone: (images: any[], photoId: string) => void;
   photoId: string;
   sx?: SxProps;
   checkSubscription?: () => boolean;
   preset?: string;
   settings?: EnhancementSettings;
}) => {
   const { enhanceImage: enhanceImageApi } = useImageTransform();
   const [isLoading, setIsLoading] = useState(false);

   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarMessage, setSnackbarMessage] = useState('');

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   const DEFAULT_SETTINGS: EnhancementSettings = {
      brightness: 10, contrast: 15, sharpness: 15, saturation: 10,
      warmth: 5, shadows: 10, skinSmoothing: 0,
   };

   const handleEnhance = async () => {
      if (checkSubscription && !checkSubscription()) {
         return;
      }

      try {
         setIsLoading(true);
         const result = await enhanceImageApi({
            url: photoUrl,
            preset,
            settings: settings || DEFAULT_SETTINGS,
         });

         if (result) {
            onDone(result.images || [], photoId);
         }
         setSnackbarSeverity('success');
         setSnackbarMessage('Image enhanced successfully');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Enhance image error:', error);
         setSnackbarSeverity('error');
         setSnackbarMessage(
            error instanceof Error ? error.message : 'Error enhancing image'
         );
         setSnackbarOpen(true);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <>
         {isLoading ? (
            <Box sx={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               gap: 1.5,
               py: 2,
               width: '100%',
            }}>
               <CircularProgress sx={{ color: '#1D9E75' }} size={32} />
               <Typography variant='body2' sx={{ color: '#B0BEC5', fontSize: '13px' }}>
                  Enhancing your photo...
               </Typography>
            </Box>
         ) : (
            <IconButton
               sx={{ ...sx, width: '100%', borderRadius: '8px', p: 0, '&:hover': { backgroundColor: 'transparent' } }}
               disabled={isLoading}
               onClick={handleEnhance}
            >
               {Icon === undefined ? (
                  <Tooltip title='Enhance Photo'>
                     <IconButton
                        size='small'
                        sx={{ width: 30, height: 30, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                     >
                        <EnhanceIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                     </IconButton>
                  </Tooltip>
               ) : (
                  Icon
               )}
            </IconButton>
         )}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default AIEnhanceImageButton;
